import ObjectId from 'bson-objectid';
import {
    Button, Calendar, ChangeAvatar, Col, Dropdown, Input, Row, Spinner2, Text
} from 'components';
import { useOutsideClick } from 'core';
import { format, subYears } from 'date-fns';
import {
    Api, capitalize, nations, theme, validateEmail
} from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IrreversableModal } from '../../';
import { FavoriteSports, Header, MySports, SponsorConsent } from './';

const initialSportsData = [
  {
    sport: '',
    favorite: '',
    position: ''
  }
];

const User = ({ user, callback, closeModal, showRemoveAlert, setShowRemoveAlert }) => {
  const [name, setName] = useState(''),
    [username, setUsername] = useState(''),
    [email, setEmail] = useState(''),
    [bio, setBio] = useState(''),
    [password, setPassword] = useState(''),
    [sport, setSport] = useState(''),
    [league, setLeague] = useState(''),
    [type, setType] = useState(null),
    [avatar, setAvatar] = useState(null),
    [loading, setLoading] = useState(false),
    [errs, setErrs] = useState([]),
    [gender, setGender] = useState(''),
    [phone, setPhone] = useState(''),
    [phoneNation, setPhoneNation] = useState(nations[0]),
    [country, setCountry] = useState(''),
    [birthday, setBirthday] = useState(null),
    [showCalendar, setShowCalendar] = useState(false),
    [favoriteSports, setFavoriteSports] = useState(initialSportsData),
    [sports, setSports] = useState(initialSportsData),
    [showAlert, setShowAlert] = useState(false),
    calendarRef = useRef(),
    dispatch = useDispatch();

  useOutsideClick(calendarRef, () => showCalendar && setShowCalendar(false), [
    'react-datepicker__year-option'
  ]);

  useEffect(() => {
    if (user) {
      setName(user?.name || '');
      setUsername(user?.username || '');
      setEmail(user?.email?.includes('@') ? user.email : '');
      setBio(user?.bio || '');
      setType(user.type && user?.type[0].toUpperCase() + user?.type.slice(1));
      setGender(user.gender || '');
      setLeague(user.league || '');
      setPhone(user.phone || '');
      setPhoneNation(nations.find((x) => x.icon === user.phoneNation) || nations[0]);
      setCountry(nations.find((x) => x.value?.toLowerCase() === user?.country?.toLowerCase()));
      setBirthday(user?.birthday && new Date(user.birthday));
      setFavoriteSports(user?.favoriteSports?.length ? user?.favoriteSports : initialSportsData);
      setSports(user?.sports?.length ? user?.sports : initialSportsData);
    }
  }, []);

  async function submit() {
    setLoading(true);
    setErrs([]);
    const newErrs = [];
    if (!name) newErrs.push({ input: 'name', err: 'Required' });
    if (!username) newErrs.push({ input: 'username', err: 'Required' });
    if (email && !validateEmail(email)) errs.push({ input: 'email', err: 'Not a valid email' });
    if (type !== 'fan' && !sports.length) newErrs.push({ input: 'sport', err: 'Required' });
    if (!user && !type) newErrs.push({ input: 'type', err: 'Required' });
    if (password && password.length < 8)
      newErrs.push({ input: 'password', err: 'At least 8 characters' });

    if (newErrs.length) {
      setErrs(newErrs);

      return setLoading(false);
    }

    let data = new FormData();

    data.append('name', name);
    data.append('username', username);
    data.append('email', email);
    data.append('league', league);
    data.append('bio', bio);
    data.append('type', type.toLowerCase());
    data.append('gender', gender);
    country && data.append('country', country?.value);
    data.append('sports', JSON.stringify(sports));
    data.append('favoriteSports', JSON.stringify(favoriteSports));
    if (phone) {
      data.append('phone', phone);
      data.append('phoneNation', phoneNation.icon);
    }
    user && data.append('userId', user._id);
    password && data.append('password', password);
    birthday && data.append('birthday', birthday.toISOString());

    if (avatar) {
      const avatarId = ObjectId().toString();
      data.append('avatar', avatar, `${avatarId}-avatar.png`);
      data.append('fileName', `${avatarId}-avatar.png`);
    }

    const res = await Api.post(user ? '/admin/editUser' : '/admin/createUser', data, 'createUser');

    if (!res) return setLoading(false);

    setTimeout(() => {
      setName('');
      setUsername('');
      setEmail('');
      setPassword('');
      setType(null);
      setLeague('');
      setAvatar('');
      setGender('');
      setBirthday(null);
      setCountry('');
      setSports(initialSportsData);
      setFavoriteSports(initialSportsData);

      dispatch({
        type: 'HANDLE_RES',
        payload: { res: user ? 'User updated' : 'User created' }
      });

      dispatch({
        type: user ? 'ADMIN_EDIT_USER' : 'ADMIN_ADD_USER',
        payload: res
      });

      callback && callback(res);
      closeModal && closeModal();
      setShowAlert(false);
      setLoading(false);
    }, 1000);
  }

  async function removeUser() {
    setLoading(true);

    const res = await Api.post('/users/remove', { userId: user._id });

    dispatch({
      type: 'ADMIN_REMOVE_DATA',
      payload: user._id
    });

    closeModal();
    setLoading(false);
  }

  return (
    <Col pad="24px" pos="relative">
      <Spinner2 fullScreen show={loading} />

      <Row start>
        <Col noFlex marg="0 24px 0 0" style={{ zIndex: 1, position: 'relative' }}>
          <ChangeAvatar user={user} onFileSelect={(image) => setAvatar(image)} />
        </Col>

        <Header user={user} />
      </Row>

      <Text h4 bold marg="40px 0 24px">
        User info
      </Text>

      <Input
        title="Name"
        placeholder="Name..."
        onChange={({ target }) => setName(target.value)}
        value={name}
        error={errs.find((x) => x.input === 'name')?.err}
      />

      <Input
        marg="24px 0 0"
        placeholder="Username..."
        title="Username"
        onChange={({ target }) => setUsername(target.value)}
        value={username}
        error={errs.find((x) => x.input === 'username')?.err}
      />

      <Input
        marg="24px 0 0"
        title="Email"
        placeholder="Email..."
        onChange={({ target }) => setEmail(target.value)}
        value={email}
        error={errs.find((x) => x.input === 'email')?.err}
      />

      <Input
        marg="24px 0 0"
        title="Password"
        password
        placeholder="***********"
        onChange={({ target }) => setPassword(target.value)}
        error={errs.find((x) => x.input === 'password')?.err}
        value={password}
      />

      <Text marg="24px 16px 4px" b2 med>
        Type
      </Text>

      <Dropdown
        admin
        options={['Athlete', 'Team', 'Fan']}
        selected={type}
        handleSelect={(e) => setType(e)}
        placeholder="Type"
        bg="#fff"
        rounded
        wid="100%"
        error={errs.find((x) => x.input === 'type')?.err}
      />

      {type && type.toLowerCase() !== 'fan' && (
        <React.Fragment>
          <Input
            marg="24px 0 0"
            title="League or division"
            placeholder="League or division..."
            value={league}
            onChange={({ target }) => setLeague(target.value)}
          />
        </React.Fragment>
      )}

      <Input
        title="Bio"
        marg="24px 0 0"
        multiline
        placeholder="Bio..."
        ht="100px"
        value={bio}
        onChange={({ target }) => setBio(target.value)}
      />

      {/* Read-only, and only when editing — the create form has no consent to show. */}
      {user && <SponsorConsent user={user} />}

      <Text h4 bold marg="40px 0 24px">
        Personal info
      </Text>

      <Dropdown
        title="Gender"
        admin
        options={['Female', 'Male', 'Other']}
        selected={gender}
        handleSelect={(e) => setGender(e)}
        placeholder="Gender..."
        bg="#fff"
        rounded
        wid="100%"
        error={errs.find((x) => x.input === 'type')?.err}
      />

      <Dropdown
        title="Birthday"
        marg="24px 0 0"
        admin
        handleSelect={(e) => setSport(e.value)}
        onClick={() => setShowCalendar(!showCalendar)}
        selected={birthday && format(new Date(birthday), 'dd MMMM, yyyy')}
        placeholder="Date of birth..."
        bg="#fff"
        rounded
        wid="100%"
        error={errs.find((x) => x.input === 'sport')?.err}
      />

      {showCalendar && (
        <Row start reverse wid="100%" ht="0">
          <Col
            pos="relative"
            pad="8px"
            bg="#fff"
            shadow
            hasRadius="16px"
            top="0"
            noFlex
            ref={calendarRef}
          >
            <Calendar
              showYearDropdown
              yearDropdownItemNumber={100}
              minDate={subYears(new Date(), 100)}
              selected={new Date(birthday || new Date())}
              maxDate={new Date()}
              onChange={(date) => {
                setBirthday(date);
                setShowCalendar(false);
              }}
            />
          </Col>
        </Row>
      )}

      <Dropdown
        title="Country"
        marg="24px 0 0"
        admin
        options={nations}
        selected={country}
        handleSelect={(e) => setCountry(e)}
        placeholder="Country..."
        bg="#fff"
        rounded
        wid="100%"
        error={errs.find((x) => x.input === 'type')?.err}
      />

      <Input
        title="Phone number"
        marg="24px 0 0"
        placeholder="Phone number..."
        onChange={({ target }) => setPhone(target.value)}
        phone
        nation={phoneNation}
        handleNationSelect={setPhoneNation}
        value={phone}
        error={errs.find((x) => x.input === 'phone')?.err}
      />

      <FavoriteSports data={favoriteSports} setData={setFavoriteSports} />

      <MySports data={sports} setData={setSports} type={type} />

      <Row marg="56px 0 0">
        <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

        <Button
          big
          onClick={!user || user?.type === type?.toLowerCase() ? submit : () => setShowAlert(true)}
          text={user ? 'Edit user' : 'Create user'}
          bg={theme.SECONDARY}
          action="createUser"
        />
      </Row>

      <IrreversableModal
        visible={showAlert}
        onConfirm={submit}
        closeModal={() => setShowAlert(false)}
        text="Be cautious, this change will also effect any of the content related to this user"
        heading={
          user &&
          `Are you sure you want to change this user's type from ${capitalize(user.type)} to ${type}?`
        }
      />

      <IrreversableModal
        visible={showRemoveAlert}
        onConfirm={removeUser}
        closeModal={() => setShowRemoveAlert(false)}
        text="Be cautious, this change will also effect any of the content related to this user"
        heading={`Are you sure you want to delete this user?`}
      />
    </Col>
  );
};

export default User;
